import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function EastBrunswickMedicalCentrePage() {
  useSEO({
    title: 'East Brunswick Medical Centre Strategy | Design For',
    description: 'Strategic consulting for primary care medical practice. Research-driven strategy for patient experience and practice operations.',
    keywords: 'primary care, medical practice, patient experience, healthcare strategy, practice management',
    canonical: '/work/east-brunswick-medical-centre',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Strategic consulting for primary care medical practice',
    description: 'We partnered with East Brunswick Medical Centre to develop strategic frameworks for patient experience, practice operations, and healthcare delivery.',
    url: 'https://example.com/work/east-brunswick-medical-centre',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Medical practice - Primary care strategy',
    keywords: 'primary care, medical practice, patient experience, healthcare strategy, practice management',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Changing the way communities access health care and visit their doctor
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">East Brunswick Medical Centre</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                East Brunswick Medical Centre wanted to respond to the changing needs of its diverse and growing community, while making it easier for patients to access care and for staff to manage demand. The challenge was improving access and triage without losing the personal feel of a local practice.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Working closely with doctors, nurses, staff, and community members, we explored new service models and prototyped them directly in the clinic environment to test what would genuinely work.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Facilitated a 40-person design jam with staff, clinicians, and patients',
                  'Prototyped new service concepts directly in the medical centre',
                  'Designed a patient-to-patient buddy system and new triage approach',
                  'Achieved an 18% increase in patient engagement',
                  'Attracted new interest from junior doctors to join the practice',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/65e4b3a231f765d88a983851ba9becb4.jpeg"
                  alt="East Brunswick Medical Centre design jam session"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/de913197e8bc32dc37cc59f2b8502ab4.jpeg"
                  alt="East Brunswick Medical Centre service prototyping"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="east-brunswick-medical-centre" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
